import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import ReportingType, {
  IReportingType
} from "../../model/reporting/ReportingType";
import CreateReportingTypeInteractor from "../../interactor/reporting/CreateReportingTypeInteractor";
import GetReportingTypeInteractor from "../../interactor/reporting/GetReportingTypeInteractor";
import UpdateReportingTypeInteractor from "../../interactor/reporting/UpdateReportingTypeInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import { IFormTypesGroupCategory } from "../../model/formconfiguration/FormTypesGroupCategory";
import ReportingTypePresentationModel, {
  TReportingTypePresentationModel
} from "../reporting/model/ReportingTypePresentationModel";

export interface TReportingTypePresenter extends TLoadingAwarePresenter {
  reportingTypes: TReportingTypePresentationModel[];
  formTypesGroupCategories: IFormTypesGroupCategory[];
}

export interface IReportingTypePresenter
  extends TReportingTypePresenter,
    TPresentable {
  load(
    reportingTypes: IReportingType[],
    formTypesGroupCategories: IFormTypesGroupCategory[]
  ): void;
  createReportingType(data: TReportingTypePresentationModel): void;
  getReportingType(): void;
  updateReportingType(data: TReportingTypePresentationModel): void;
}

export interface IReportingTypePresentationModel extends IReportingType {
  key: string;
}

const defaultState: TReportingTypePresenter = {
  reportingTypes: [],
  formTypesGroupCategories: []
};

function formatReportingType(data: any, reportingType?: IReportingType) {
  let reportingTypeRecord = reportingType || ReportingType(data);
  return reportingTypeRecord;
}

const ReportingTypePresenter = withStore<
  IReportingTypePresenter,
  TReportingTypePresenter
>(({ application, store, loader, translate }): IReportingTypePresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TReportingTypePresenter>();

  loader.start("reportingTypesLoader");

  const load = (
    reportingTypes: IReportingType[],
    formTypesGroupCategories: IFormTypesGroupCategory[]
  ) => {
    _store.update({
      reportingTypes: [
        ..._store.getState<TReportingTypePresenter>().reportingTypes,
        ...reportingTypes.map(ReportingTypePresentationModel)
      ],
      formTypesGroupCategories
    });
    loader.stop("reportingTypesLoader");
  };

  const getReportingType = async () => {
    const reportingType = await _application.container
      .resolve<GetReportingTypeInteractor>("getReportingType")
      .execute();
    _store.update({
      reportingTypes: [...reportingType.map(ReportingTypePresentationModel)]
    });
  };

  const setReportingTypeFields = (data: TReportingTypePresentationModel) => {
    let reportingType = formatReportingType(data);
    if (reportingType.repeatingPeriod) {
      reportingType.npPeriodFrom = undefined;
      reportingType.npPeriodTo = undefined;
    } else {
      reportingType.periodOfRepetiton = undefined;
      reportingType.periodOfRepetitonMeasureUnit = undefined;
    }

    return reportingType;
  };

  const createReportingType = async (data: TReportingTypePresentationModel) => {
    try {
      const reportingType = setReportingTypeFields(data);
      await _application.container
        .resolve<CreateReportingTypeInteractor>("createReportingType")
        .execute(reportingType);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate(
            "reporting_types_page.messages.create_reportingType_success"
          )
        );
      await getReportingType();
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const updateReportingType = async (data: TReportingTypePresentationModel) => {
    try {
      const reportingType = setReportingTypeFields(data);
      await _application.container
        .resolve<UpdateReportingTypeInteractor>("updateReportingType")
        .execute(reportingType);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate(
            "reporting_types_page.messages.update_reportingType_success"
          )
        );
      await getReportingType();
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  return {
    ...state,

    store: _store,
    loader,
    application: _application,
    translate,
    load,
    createReportingType,
    getReportingType,
    updateReportingType
  };
}, defaultState);
export default ReportingTypePresenter;
